import React, { Component } from 'react';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import { injectIntl, FormattedMessage } from '@atlassian/jira-intl';
import {
	SpotlightTransition,
	Spotlight,
	AutoDismissFlag,
	FlagGroup,
	colors,
	ModalDialog,
	ModalBody,
	Button,
	ButtonTheme,
	CustomThemeButton,
	modalButtonTheme as onboardingModalButtonTheme,
} from '@atlassian/jira-portfolio-3-common';
import DocLink from '@atlassian/jira-portfolio-3-portfolio/src/common/view/doc-link';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';

import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, State } from './types';

// eslint-disable-next-line jira/react/no-class-components
class Onboarding extends Component<Props, State> {
	state = {
		showCompleteFlag: false,
	};

	setShowCompleteFlag = (showCompleteFlag: boolean) => {
		this.setState({ showCompleteFlag });
	};

	render() {
		const { activeCard, intl, introOpen, setActiveCard, setIsIntroOpen, setOnBoardingShown } =
			this.props;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let resultingCards: Record<string, any> = {};

		const moveNext = () => {
			const currentCardIndex =
				activeCard === '' ? -1 : Object.keys(resultingCards).indexOf(activeCard);
			const nextCard = Object.keys(resultingCards)[currentCardIndex + 1];
			setActiveCard(nextCard);
		};

		const startTour = () => {
			setOnBoardingShown(true);
			setIsIntroOpen(false);
			moveNext();
		};

		const skipTour = () => {
			setIsIntroOpen(false);
			this.setShowCompleteFlag(true);
			setOnBoardingShown(true);
		};

		const actionsNextButton = [
			{
				onClick: moveNext,
				text: intl.formatMessage(messages.nextButton),
			},
		];

		const cards = {
			'update-jira': {
				actions: actionsNextButton,
				heading: intl.formatMessage(messages.updateJiraCardTitle),
				dialogPlacement: 'bottom left',
				target: 'update-jira',
				targetBgColor: token('elevation.surface', 'white'),
				targetRadius: 3,
				content: (
					<div>
						{intl.formatMessage(messages.updateJiraCardContent)}
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.gutter}>
							{intl.formatMessage(messages.updateJiraCardAdditionalContent)}
						</div>
					</div>
				),
			},
			'view-tab-switching': {
				actions: [
					{
						onClick: moveNext,
						text: intl.formatMessage(messages.nextButton),
					},
					{
						onClick: () => {
							setActiveCard('');
							this.setShowCompleteFlag(true);
						},
						text: intl.formatMessage(commonMessages.learnMore),
						appearance: 'subtle-link',
						href: DocLink.URLs.viewSwitching,
						target: 'blank',
					},
				],
				heading: intl.formatMessage(messages.tabSwitchingCardTitle),
				dialogPlacement: 'bottom left',
				target: 'view-tab-switching',
				targetBgColor: token('elevation.surface', 'white'),
				content: intl.formatMessage(messages.tabSwitchingCardContent),
			},
			'filters-view-settings': {
				actions: actionsNextButton,
				heading: intl.formatMessage(messages.filtersViewSettingsCardTitle),
				dialogPlacement: 'bottom left',
				target: 'filters-view-settings',
				targetBgColor: token('elevation.surface', 'white'),
				content: intl.formatMessage(messages.filtersViewSettingsCardContent),
			},
			'view-actions': {
				actions: actionsNextButton,
				heading: intl.formatMessage(messages.savedViewsDropdownCardTitle),
				dialogPlacement: 'bottom left',
				target: 'view-actions',
				targetBgColor: token('elevation.surface', 'white'),
				content: intl.formatMessage(messages.savedViewsDropdownCardContent),
			},
			scope: {
				actions: actionsNextButton,
				heading: intl.formatMessage(messages.scopeCardTitle),
				dialogPlacement: 'right top',
				target: 'scope',
				targetBgColor: token('elevation.surface', 'white'),
				content: (
					<div>
						{intl.formatMessage(messages.scopeCardContent)}
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.gutter}>
							<FormattedMessage
								{...messages.scopeCardAdditionalContent}
								values={{
									link: (
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										<DocLink className={styles.hierarchyLink} href={DocLink.URLs.gettingStarted}>
											{intl.formatMessage(messages.scopeCardAdditionalContentHierarchyLink)}
										</DocLink>
									),
								}}
							/>
						</div>
					</div>
				),
			},
			fields: {
				actions: actionsNextButton,
				heading: intl.formatMessage(messages.fieldsCardTitle),
				dialogPlacement: 'left top',
				target: 'fields',
				targetBgColor: token('elevation.surface', 'white'),
				content: (
					<div>
						{intl.formatMessage(messages.fieldsCardContent)}
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.gutter}>
							{intl.formatMessage(messages.fieldsCardAdditionalContent)}
						</div>
					</div>
				),
			},
			timeline: {
				actions: [
					{
						onClick: () => {
							setActiveCard('');
							this.setShowCompleteFlag(true);
						},
						text: intl.formatMessage(messages.finishButton),
					},
				],
				heading: intl.formatMessage(messages.timelineCardTitle),
				dialogPlacement: 'right top',
				target: 'timeline',
				targetBgColor: token('elevation.surface', 'white'),
				content: (
					<div>
						{intl.formatMessage(messages.timelineCardContent)}
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<div className={styles.gutter}>
							{intl.formatMessage(messages.timelineCardAdditionalContent)}
						</div>
					</div>
				),
			},
		};

		resultingCards = cards;

		const activeCardIndex = Object.keys(resultingCards).indexOf(activeCard);
		const activeCardComponent = resultingCards[activeCard];

		return (
			<>
				{introOpen && (
					<ModalDialog
						autoFocus
						// Replace with lodash/noop
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onClose={() => {}}
						shouldCloseOnOverlayClick={false}
						shouldCloseOnEscapePress={false}
						testId="portfolio-3-portfolio.app-simple-plans.onboarding.benefits-modal"
					>
						<ModalBody>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
							<div className={styles.dialogContent}>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles.banner} />

								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles.dialogInnerContent}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.contentHeader}>
										{intl.formatMessage(messages.plansWelcomeModalTitle)}
									</div>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<p className={styles.contentText}>
										{intl.formatMessage(messages.plansWelcomeModalContentSpork)}
									</p>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.buttonBar}>
										<Button
											appearance="subtle"
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className={styles.skipButton}
											onClick={skipTour}
											testId="portfolio-3-portfolio.app-simple-plans.onboarding.skip-tour"
										>
											{intl.formatMessage(messages.welcomeModalSkipTour)}
										</Button>
										<ButtonTheme.Provider value={onboardingModalButtonTheme}>
											<CustomThemeButton
												appearance="primary"
												onClick={startTour}
												testId="portfolio-3-portfolio.app-simple-plans.onboarding.get-started"
											>
												{intl.formatMessage(messages.welcomeModalGetStarted)}
											</CustomThemeButton>
										</ButtonTheme.Provider>
									</div>
								</div>
							</div>
						</ModalBody>
					</ModalDialog>
				)}
				{this.state.showCompleteFlag && (
					<FlagGroup
						onDismissed={() => {
							this.setShowCompleteFlag(false);
						}}
					>
						<AutoDismissFlag
							id="portfolio-3-app-simple-plans.onboarding-success"
							title={intl.formatMessage(messages.doneFlagTitle)}
							description={intl.formatMessage(messages.plansDoneFlagContent)}
							icon={
								<SuccessIcon label="" primaryColor={token('color.icon.success', colors.G300)} />
							}
							actions={[
								{
									content: ff('in-product-help-for-plans_qwds9') ? (
										<HelpPanelLink
											fallbackComponent={
												<DocLink href={DocLink.URLs.gettingStarted}>
													{intl.formatMessage(messages.doneFlagViewDocumentationLink)}
												</DocLink>
											}
											appearance="link"
											spacing="none"
										>
											{intl.formatMessage(messages.doneFlagViewDocumentationLink)}
										</HelpPanelLink>
									) : (
										<DocLink href={DocLink.URLs.gettingStarted}>
											{intl.formatMessage(messages.doneFlagViewDocumentationLink)}
										</DocLink>
									),
								},
							]}
							testId="portfolio-3-portfolio.app-simple-plans.onboarding.success-flag"
						/>
					</FlagGroup>
				)}
				{activeCardComponent && (
					<SpotlightTransition>
						{}
						<Spotlight
							heading={activeCardComponent.heading}
							actions={activeCardComponent.actions}
							dialogPlacement={activeCardComponent.dialogPlacement}
							target={activeCardComponent.target}
							targetBgColor={activeCardComponent.targetBgColor}
							targetRadius={activeCardComponent.targetRadius}
							dialogWidth={300}
							actionsBeforeElement={
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								<span className={styles.steps}>{`${activeCardIndex + 1}/${
									Object.keys(resultingCards).length
								}`}</span>
							}
							testId={`portfolio-3-portfolio.app-simple-plans.onboarding.spotlight-card-${
								activeCardIndex + 1
							}`}
						>
							{activeCardComponent.content}
						</Spotlight>
					</SpotlightTransition>
				)}
			</>
		);
	}
}

export default injectIntl(Onboarding);
